import { FC, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Box, Button, Callout, Link, Tabs, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceReviewerStrategiesStrategyEnumValues,
} from 'generated/resources/datatable.graphql'
import {
  PerformanceQuestionSingleLabelInput,
  PerformanceQuestionnaireElementInput,
  PerformanceRatingScaleOption,
  PerformanceScoreConfigurationFragment,
  PerformanceValueObjectsQuestionnaireElementTypesEnum,
  PerformanceValueObjectsQuestionnaireableEnum,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { Result, UnhandledResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isEnumValue } from 'modules/core/lib/utils/enums'
import app from 'modules/core/store/models/app'

import { BoxedCallout } from 'modules/performance_process/components/common/BoxedCallout'
import { GenerateUsingAIModal } from 'modules/performance_process/components/review/show/GenerateUsingAI/GenerateUsingAIModal'
import { GenerateUsingAIStandaloneForm } from 'modules/performance_process/components/review/show/GenerateUsingAI/GenerateUsingAIStandaloneForm'
import tabsPicker from 'modules/performance_process/components/review/show/helpers/tabsPicker'
import { ScoreWeightsSidepanel } from 'modules/performance_process/components/score_configuration_weights/ScoreWeightsSidepanel'
import { WeightTag } from 'modules/performance_process/components/score_configuration_weights/WeightTag'
import {
  getQuestionnaireTranslations,
  useMappedQuestionnaires,
} from 'modules/performance_process/hooks/useMappedQuestionnaires'
import { type PerformanceRawQuestionnairesEntity } from 'modules/performance_process/hooks/useQuestionnairesRawContent'
import {
  configurationByStrategy,
  useSectionWeights,
  useStrategyWeights,
} from 'modules/performance_process/hooks/useStrategiesScoreConfiguration'
import performanceWeightsUpsell from 'modules/performance_process/lib/assets/performanceWeightsUpsell.png'
import EditRatingModal, {
  type HandleSubmitRating,
} from 'modules/questionnaires/components/EditRatingModal'
import Questionnaire from 'modules/questionnaires/components/Questionnaire'
import type { QuestionnaireContent } from 'modules/questionnaires/lib/types'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

export type HandleUpdateQuestionnaire = (
  questionnaireContent: PerformanceQuestionnaireElementInput[],
  strategy: PerformanceReviewerStrategiesStrategyEnum
) => Promise<{ success: boolean }>

type HandleSubmitGenerateUsingAI = (
  content: string,
  strategy: PerformanceReviewerStrategiesStrategyEnum
) => Promise<UnhandledResult<Result<unknown>>>

interface Props {
  questionnaireableId: number
  questionnaireableType: PerformanceValueObjectsQuestionnaireableEnum
  reviewType: PerformanceReviewerStrategiesStrategyEnum[]
  questionnaires: PerformanceRawQuestionnairesEntity
  defaultScoringRange: PerformanceRatingScaleOption[]
  onUpdateQuestionnaire: HandleUpdateQuestionnaire
  updatingQuestionnaire: boolean
  handleSubmitRating: HandleSubmitRating
  handleSubmitGenerateUsingAI: HandleSubmitGenerateUsingAI
  generateQuestionsUsingAiEnabled?: boolean
  scoreConfiguration?: PerformanceScoreConfigurationFragment | null
  hasCustomScoreConfigurationEnabled: boolean
}

export const ReviewTypeQuestions: FC<Props> = ({
  questionnaireableId,
  questionnaireableType,
  reviewType,
  questionnaires,
  defaultScoringRange,
  onUpdateQuestionnaire,
  updatingQuestionnaire = false,
  handleSubmitRating,
  handleSubmitGenerateUsingAI,
  generateQuestionsUsingAiEnabled = false,
  scoreConfiguration,
  hasCustomScoreConfigurationEnabled,
}) => {
  const { navigation } = useNavigation()
  const tabs = tabsPicker(reviewType)
  const [currentTab, setCurrentTab] = useState<PerformanceReviewerStrategiesStrategyEnum>(
    tabs[0]?.id || 'self'
  )
  const validCurrentTab: PerformanceReviewerStrategiesStrategyEnum =
    tabs.find((t) => t.id == currentTab)?.id ?? tabs[0]?.id ?? 'self'

  const { mappedQuestionnairesByStrategies, totalQuestionsByStrategies } =
    useMappedQuestionnaires(questionnaires)
  const currentQuestionnaires = mappedQuestionnairesByStrategies[validCurrentTab]

  const strategyConfigurations = configurationByStrategy(scoreConfiguration)
  const { hasCustomWeights, weightsBySectionId, isSectionWeightVisible } = useStrategyWeights({
    strategy: validCurrentTab,
    scoreConfiguration,
  })
  const { isSectionWeightable } = useSectionWeights({
    questionnaire: currentQuestionnaires,
  })

  const showUpsellingKit = !hasCustomScoreConfigurationEnabled

  const [searchParams, setSearchParams] = useSearchParams()
  const showScoreWeightsSidepanel = searchParams.get('sectionWeightsSidepanel') === 'open'

  const [scoringRangeModalOpen, setScoringRangeModalOpen] = useState(false)
  const [generateUsingAIModalOpen, setGenerateUsingAIModalOpen] = useState(false)
  const handleTabClick = (id: PerformanceReviewerStrategiesStrategyEnum) => {
    const tab = tabs.find((t) => t.id == id)
    if (tab) {
      setCurrentTab(id)
    }
    return null
  }

  const handleQuestionnaireChange = async (content: QuestionnaireContent) => {
    const questionnaireElements = content.map((item) => {
      const isSection = 'questions' in item
      const element: {
        type: PerformanceValueObjectsQuestionnaireElementTypesEnum
        uuid: string
        sectionTitle?: string
        questions: PerformanceQuestionSingleLabelInput[]
      } = {
        type: isSection ? 'section' : 'question',
        uuid: item.uuid,
        sectionTitle: isSection ? item.title : undefined,
        questions: isSection ? item.questions : [item],
      }
      return element
    })

    const result = await onUpdateQuestionnaire(questionnaireElements, validCurrentTab)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return content
  }

  const hasQuestions = totalQuestionsByStrategies[validCurrentTab] > 0

  const renderSectionHeader = useUnsafeCallback(
    (sectionId: string) => {
      if (isSectionWeightVisible) {
        const weight = weightsBySectionId[sectionId] ?? 0
        const weightable = isSectionWeightable(sectionId)

        return <WeightTag weight={weight} weightable={weightable} />
      }
    },
    [weightsBySectionId, isSectionWeightable, isSectionWeightVisible]
  )

  const getTabTag = (tab: PerformanceReviewerStrategiesStrategyEnum) => {
    const questionsCount = totalQuestionsByStrategies[tab].toString()
    const strategyWeight = strategyConfigurations[tab]?.weight || 0
    return hasCustomScoreConfigurationEnabled && hasCustomWeights
      ? `${strategyWeight}%`
      : questionsCount
  }

  const hasFinalScoreQuestion = scoreConfiguration?.mode === 'final_question'
  const hasPotentialScoreQuestion = Boolean(
    questionnaires.employeePotentialScoreManagerQuestionnaire
  )
  const hasCompetenciesAssessments = Boolean(
    questionnaires.competenciesPlaceholderManagerQuestionnaire ||
      questionnaires.competenciesPlaceholderSelfQuestionnaire
  )

  return (
    <>
      {scoringRangeModalOpen && (
        <EditRatingModal
          translations={{
            title: i18n.t('performance_process.reviews.scoring_range'),
            description: i18n.t('performance_process.reviews.scoring_range_description'),
            save: i18n.t('common.save'),
            cancel: i18n.t('common.cancel'),
          }}
          onClose={() => setScoringRangeModalOpen(false)}
          defaultRatingScale={defaultScoringRange}
          handleSubmit={handleSubmitRating}
        >
          {(hasCompetenciesAssessments || hasPotentialScoreQuestion || hasFinalScoreQuestion) && (
            <Callout type="info" hideIcon>
              <Box gap="s8">
                <Text.Normal color="textPrimary">
                  {i18n.t('performance_process.reviews.scoring_range_callout.description')}
                </Text.Normal>
                <Link.Router to={navigation.settings.performanceProcess.fullPath()} type="solid">
                  {i18n.t('performance_process.reviews.scoring_range_callout.link')}
                </Link.Router>
              </Box>
            </Callout>
          )}
        </EditRatingModal>
      )}
      {generateUsingAIModalOpen && (
        <GenerateUsingAIModal
          onClose={() => setGenerateUsingAIModalOpen(false)}
          onGenerate={(content: string) => handleSubmitGenerateUsingAI(content, validCurrentTab)}
        />
      )}

      {hasCustomWeights && showScoreWeightsSidepanel && (
        <ScoreWeightsSidepanel
          open
          questionnaireableId={questionnaireableId}
          questionnaireableType={questionnaireableType}
          reviewTypes={reviewType}
          questionnaires={questionnaires}
          scoreConfiguration={scoreConfiguration}
          hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
          onClose={() => setSearchParams({})}
        />
      )}

      <Box flexDirection="row" gap="s32">
        <Box gap="s24" width="full">
          <Box position="relative" width="full">
            <Box width="full">
              <Tabs
                key={'question-tabs'}
                currentId={validCurrentTab}
                onTabClicked={(id) =>
                  isEnumValue(PerformanceReviewerStrategiesStrategyEnumValues, id) &&
                  handleTabClick(id)
                }
              >
                {tabs.map((tab) => (
                  <Tabs.Tab key={tab.id} id={tab.id} label={tab.label} tag={getTabTag(tab.id)} />
                ))}
              </Tabs>
            </Box>

            <Box position="absolute" style={{ bottom: '0', right: '0' }} paddingBottom="s6">
              <Box flexDirection="row" gap="s12">
                {showUpsellingKit ? (
                  <UpsellingKit.ProductWidgetPopover
                    label={i18n.t('performance_process.section_weights.show_weight_range')}
                    mediaUrl={performanceWeightsUpsell}
                    variant="outlinePromote"
                    title={i18n.t('performance_process.upsells.weights.title_review')}
                    description={i18n.t('performance_process.upsells.weights.description')}
                    requestSource="Performance - weights enterprise plan - draft mode"
                    moduleRequested={AvailableModules.review_process}
                    featureRequested={Features.review_process_custom_score_configuration}
                    requestReason={ValidReasons.MissingFeature}
                    fallbackComponent={
                      hasCustomWeights && (
                        <Button
                          type="secondary"
                          iconName="calculator"
                          onClick={() => setSearchParams({ sectionWeightsSidepanel: 'open' })}
                          disabled={updatingQuestionnaire}
                        >
                          {i18n.t('performance_process.section_weights.show_weight_range')}
                        </Button>
                      )
                    }
                  />
                ) : (
                  hasCustomWeights && (
                    <Button
                      type="secondary"
                      iconName="calculator"
                      onClick={() => setSearchParams({ sectionWeightsSidepanel: 'open' })}
                      disabled={updatingQuestionnaire}
                    >
                      {i18n.t('performance_process.section_weights.show_weight_range')}
                    </Button>
                  )
                )}

                <Button
                  type="secondary"
                  onClick={() => setScoringRangeModalOpen(true)}
                  iconName="settings"
                  disabled={updatingQuestionnaire}
                >
                  {i18n.t('performance_process.reviews.edit_scoring_range')}
                </Button>
              </Box>
            </Box>
          </Box>

          <Box gap="s16">
            <>
              <Questionnaire
                key={`questionnaire-questions-${validCurrentTab}`}
                translations={getQuestionnaireTranslations()}
                content={currentQuestionnaires.general}
                onChange={handleQuestionnaireChange}
                renderSectionHeaderAddon={renderSectionHeader}
                extraActions={
                  generateQuestionsUsingAiEnabled && hasQuestions
                    ? [
                        {
                          label: i18n.t('performance_process.reviews.generate_using_ai'),
                          icon: 'ai',
                          type: 'primary',
                          onClick: () => setGenerateUsingAIModalOpen(true),
                          disabled: false,
                        },
                      ]
                    : undefined
                }
              />

              {generateQuestionsUsingAiEnabled && !hasQuestions && (
                <Box alignItems="center">
                  <Box justifyContent="center" alignItems="center" gap="s8" width="s400">
                    <Text.Normal>
                      {i18n.t('performance_process.reviews.generate_using_ai_description')}
                    </Text.Normal>
                    <Box justifyContent="center" alignItems="center" gap="s16" width="full">
                      <GenerateUsingAIStandaloneForm
                        onGenerate={(content: string) =>
                          handleSubmitGenerateUsingAI(content, validCurrentTab)
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </>

            {!!currentQuestionnaires.competencies.length && (
              <Questionnaire
                key={`questionnaire-competencies-placeholder-${validCurrentTab}`}
                translations={getQuestionnaireTranslations()}
                content={currentQuestionnaires.competencies}
                onChange={handleQuestionnaireChange}
                renderSectionHeaderAddon={renderSectionHeader}
                showActions={false}
              />
            )}

            {!!currentQuestionnaires.employeeScore.length && (
              <Questionnaire
                key={`questionnaire-score-${validCurrentTab}`}
                translations={getQuestionnaireTranslations()}
                content={currentQuestionnaires.employeeScore}
                onChange={handleQuestionnaireChange}
                renderSectionHeaderAddon={renderSectionHeader}
                showActions={false}
              />
            )}

            {!!currentQuestionnaires.employeePotentialScore.length && (
              <Questionnaire
                key={`questionnaire-potential-score-${validCurrentTab}`}
                translations={getQuestionnaireTranslations()}
                content={currentQuestionnaires.employeePotentialScore}
                onChange={handleQuestionnaireChange}
                renderSectionFooter={() => (
                  <BoxedCallout
                    description={i18n.t(
                      'performance_process.employee_potential_score.questionnaire.callout'
                    )}
                  />
                )}
                showActions={false}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}
